import {GAME_IFRAME_ID} from '@/services/internal/sdk/sdk';
import {useRef, useState, useEffect} from 'react';
import startPublish from './startPublish';
import stopPublish from './stopPublish';
import {getEnvConfig} from '@/utils/getEnvConfig';

export const useReplayRecording = (replaySettings) => {
  const peerConnectionRef = useRef<RTCPeerConnection | undefined>(undefined);
  const wsConnectionRef = useRef<WebSocket | undefined>(undefined);
  const [localStream, setLocalStream] = useState<MediaStream | undefined>(undefined);

  const userData = {name: replaySettings?.login, password: replaySettings?.password};
  const wsURL = getEnvConfig().wowza;
  const streamInfo = {
    userData,
    applicationName: 'webrtc',
    streamName: '',
    signalingURL: wsURL,
  };

  const callbacks = {
    onError: (error) => {
      console.error('Replay: Publish error:', error);
    },
    onConnectionStateChange: (state) => {
      console.log('Replay: Connection state changed.', state);
    },
    onSetPeerConnection: (data) => {
      peerConnectionRef.current = data.peerConnection;
    },
    onSetWebsocket: (data) => {
      wsConnectionRef.current = data.websocket;
    },
    onSetSenders: (data) => {
      console.log('Replay: Senders set.', data);
    },
  };

  // Short-lived TURN relay creds for clients whose network blocks Wowza's :1935.
  // Best-effort: on any failure we publish over the direct path (no regression).
  const fetchTurnServers = async (): Promise<RTCIceServer[]> => {
    // Bound the request: a stalled TURN fetch would otherwise hold up startPublish, and the
    // direct path is a fine fallback. Abort after 5s and clear the timer either way.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5000);
    try {
      const res = await fetch('/api/turn-credentials', {signal: controller.signal});
      if (!res.ok) return [];
      const data = await res.json();
      return data?.iceServers ?? [];
    } catch (e) {
      console.warn('Replay: TURN credentials unavailable, using direct path', e);
      return [];
    } finally {
      clearTimeout(timer);
    }
  };

  const startStreaming = async (playersTournamentPlayerId) => {
    streamInfo.streamName = String(playersTournamentPlayerId);

    console.log('Replay: Starting streaming....');
    const iframe = document.getElementById(GAME_IFRAME_ID) as HTMLIFrameElement;
    if (iframe && iframe.contentWindow) {
      const stream = iframe.contentWindow.document.querySelector('canvas')?.captureStream(60);
      if (stream) {
        setLocalStream(stream);

        const iceServers = await fetchTurnServers();
        startPublish(
          {...streamInfo, videoTrack: stream.getVideoTracks()[0], iceServers},
          null,
          callbacks
        );
      } else {
        console.warn('Replay: Failed to capture stream from game canvas.');
      }
    } else {
      console.warn('Replay: Game iframe or content window not found.');
    }
  };

  const stopStreaming = () => {
    stopPublish(peerConnectionRef.current, wsConnectionRef.current, callbacks);
    peerConnectionRef.current = undefined;
    wsConnectionRef.current = undefined;
    if (localStream) {
      localStream.getTracks().forEach((track) => track.stop());
      setLocalStream(undefined);
    }
  };

  useEffect(() => {
    return () => {
      stopStreaming();
    };
  }, []);

  return {startStreaming, stopStreaming};
};
